import React, { useState } from "react";
import { useSnackbar } from "notistack";
import { Button, DialogActions, DialogContent, DialogTitle, Dialog, LinearProgress, InputAdornment, IconButton } from "@material-ui/core";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import { Clear as ClearIcon } from "@material-ui/icons";
import MomentUtils from "@date-io/moment";
import { exportInvoices } from "api/owner/reports";
import { ClubSelect } from "components";
import i18n from "./i18n";

// prop types
interface ExportInvoicesDialogProps {
    clubId?: string,
    dateInInterval: string | null,
    showDialog: boolean,
    onClose: () => void,
    onSetClubId: (clubId?: string) => void,
    onSetDateInInterval: (date: string | null) => void,
}

// export and filter invoices dialog
const ExportInvoicesDialog = ({ clubId, dateInInterval, showDialog, onClose, onSetClubId, onSetDateInInterval }: ExportInvoicesDialogProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        i18n.errors[error.type] || `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);

    // clear date
    const clearDate = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSetDateInInterval(null);
    };

    // export invoices
    const startExport = async () => {
        setShowLoadingAnimation(true);
        try {
            const { email } = await exportInvoices({
                clubId,
                dateInInterval: dateInInterval || undefined,
                reportingInterval: (dateInInterval && "month") || undefined,
            });
            onClose();
            enqueueSnackbar(i18n.thisMayTakeAWhile(email), { variant: "info", autoHideDuration: 10000 });
        } catch (error) {
            displayError(error);
        }
        setShowLoadingAnimation(false);
    };

    // render
    return (
        <Dialog open={showDialog} onClose={onClose}>
            <DialogTitle>{i18n.export}</DialogTitle>
            <DialogContent dividers>

                {/* club filter */}
                <ClubSelect
                    clubId={clubId}
                    controlProps={{ fullWidth: true, margin: "normal" }}
                    userRoles={["owner"]}
                    onChange={onSetClubId}
                />

                {/* month filter */}
                <MuiPickersUtilsProvider utils={MomentUtils}>
                    <DatePicker
                        autoOk
                        format="MMMM YYYY"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={clearDate}>
                                        <ClearIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        label={i18n.timePeriod}
                        margin="normal"
                        value={dateInInterval}
                        variant="inline"
                        views={["month", "year"]}
                        onChange={(m) => onSetDateInInterval(m!.utc().startOf("month").toISOString())}
                    />
                </MuiPickersUtilsProvider>
            </DialogContent>
            {showLoadingAnimation && (
                <LinearProgress />
            )}
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    {i18n.cancel}
                </Button>
                <Button onClick={startExport} color="primary" disabled={showLoadingAnimation}>
                    {i18n.export}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExportInvoicesDialog;
